import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Icon } from './Icon';

// #150: the open menu is portaled to `document.body` and positioned `fixed` so it
// escapes the trigger cell's `relative z-[1]` stacking context. The old inline
// `absolute z-10` menu was TRAPPED inside that context and painted over by the
// `z-[3]` add-section / add-test-case affordances and the later-in-DOM sibling
// rows. `fixed` coordinates are measured from the trigger and clamped to the
// viewport so the whole menu stays reachable (FR-001/002/008/009).
const MENU_GAP = 4; // px between trigger and menu (≈ the previous `top-7` offset)
const MENU_MIN_WIDTH = 200; // matches the menu's `min-w-[200px]`
// The comfortable height of an open menu (filter input + the `max-h-56` list +
// padding). When less room than this is available below the trigger, prefer to
// flip above if there is more room there.
const MENU_PREFERRED_HEIGHT = 240;

/**
 * Where the portaled menu is pinned relative to the trigger. A discriminated
 * union so the `above` flip and the `below` default cannot mix `top`/`bottom`.
 */
export type MenuStyle =
  | {
      readonly placement: 'below';
      readonly top: number;
      readonly left: number;
      readonly maxHeight: number;
    }
  | {
      readonly placement: 'above';
      readonly bottom: number;
      readonly left: number;
      readonly maxHeight: number;
    };

/**
 * Pure positioning math for the portaled menu, measured from the trigger's
 * viewport rect. Kept separate from the component (and unit-tested directly)
 * because happy-dom has no layout engine — the flip/clamp branches are not
 * exercisable through a rendered `getBoundingClientRect` (which returns zeros).
 */
export function computeMenuStyle(
  rect: DOMRect,
  viewportWidth: number,
  viewportHeight: number,
): MenuStyle {
  // Clamp horizontally so a trigger near the right edge does not push the menu
  // off-screen (FR-009).
  const left = Math.max(MENU_GAP, Math.min(rect.left, viewportWidth - MENU_MIN_WIDTH - MENU_GAP));
  const spaceBelow = viewportHeight - rect.bottom - MENU_GAP;
  const spaceAbove = rect.top - MENU_GAP;
  // Flip above only when below is too cramped AND above genuinely has more room
  // (FR-009/SC-005); the menu is bottom-anchored so a short menu still hugs the
  // trigger. Otherwise open directly below (the established visual).
  if (spaceBelow < MENU_PREFERRED_HEIGHT && spaceAbove > spaceBelow) {
    return {
      placement: 'above',
      bottom: viewportHeight - rect.top + MENU_GAP,
      left,
      maxHeight: Math.max(0, spaceAbove),
    };
  }
  // In both branches the height is clamped to the room available so every option
  // stays reachable (the list scrolls within the clamped surface).
  return {
    placement: 'below',
    top: rect.bottom + MENU_GAP,
    left,
    maxHeight: Math.max(0, spaceBelow),
  };
}

/** Translate a {@link MenuStyle} (or the not-yet-measured `null`) to inline CSS.
 * Exported so the F-139 OwnerChooser can reuse the same portal-positioning math. */
export function menuStyleToCss(style: MenuStyle | null): CSSProperties {
  if (style === null) return { position: 'fixed' };
  const base: CSSProperties = {
    position: 'fixed',
    left: `${style.left}px`,
    maxHeight: `${style.maxHeight}px`,
  };
  return style.placement === 'below'
    ? { ...base, top: `${style.top}px` }
    : { ...base, bottom: `${style.bottom}px` };
}

export type RoleChooserProps = {
  /** Currently assigned Study Roles (full-list; may include out-of-matrix roles). */
  roles: readonly string[];
  /**
   * The trial's Allowed-Role Set. EMPTY ⇒ no matrix ⇒ the chooser is READ-ONLY:
   * pre-filled roles render as plain chips (no add/remove) and there is no upload
   * prompt — the matrix is required since F-021, so the prompt is dead (F-040 US3).
   */
  allowedRoles: readonly string[];
  /** Full-list replace on any add/remove (F-010 FR-012). */
  onChange: (next: string[]) => void;
  /** `compact` for the list table cell, `full` for the detail rail. */
  variant?: 'compact' | 'full';
  /** A label (e.g. the Test ID) so multiple choosers stay aria-distinguishable. */
  label?: string;
};

/**
 * The single, shared Study-Role chooser used by BOTH entry points (the list
 * `TestCaseTable` and the detail `InclusionPanel`) so they behave identically
 * (F-010 FR-014). Selectable roles are constrained to the trial's Allowed-Role
 * Set (AS1) — there is no free-text entry; multi-select + clear are supported
 * (AS2/AS3); roles (incl. `UNBLINDED …`) render verbatim with no badge
 * (I-RP-08). A previously-assigned role no longer in the matrix is surfaced as
 * "off-matrix" — flagged, NOT auto-removed (FR-032/AS8). With no matrix the
 * chooser is READ-ONLY (no add/remove control, no prompt — F-040 US3).
 *
 * F-041 polishes the OPEN dropdown: it dismisses on outside-click / Escape
 * (returning focus to the trigger, discarding any typed filter — FR-001/002/003),
 * renders on an opaque surface (FR-004/005), and is a WCAG type-to-filter
 * combobox with keyboard navigation (FR-006-010/019-021). Every interactive
 * control stops click propagation so the chooser is safe inside a row that
 * navigates on click.
 */
export function RoleChooser({
  roles,
  allowedRoles,
  onChange,
  variant = 'full',
  label,
}: RoleChooserProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // #150: the fixed coordinates of the portaled menu, measured from the trigger.
  // `null` until the first measurement (on open) so the menu is never positioned
  // from a stale rect.
  const [menuStyle, setMenuStyle] = useState<MenuStyle | null>(null);
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // #150: the portaled menu surface (lives under document.body, NOT inside
  // `rootRef`). The outside-click test must treat clicks here as "inside".
  const popoverRef = useRef<HTMLDivElement>(null);

  // FR-003/FR-018: closing always discards typed filter text so the menu reopens
  // clean. Stable identity (only setters) keeps effects calm.
  const close = useCallback((): void => {
    setOpen(false);
    setFilter('');
    setActiveIndex(0);
  }, []);

  // FR-001 / AS4: a click outside the chooser closes the open menu — and that
  // single dismissing click is SWALLOWED (capture-phase `stopPropagation`) so it
  // never also fires a parent row's navigation. Capture runs before any ancestor
  // handler (React delegates at the root), and the listener is removed the moment
  // the menu closes, so only the one outside click is ever intercepted.
  //
  // #150: the menu is now portaled to document.body, OUTSIDE `rootRef`, so a
  // native `.contains()` from the trigger subtree alone no longer covers an
  // in-menu click. A click is "inside" if it lands in EITHER the trigger subtree
  // (`rootRef`) OR the portaled menu surface (`popoverRef`); only a click outside
  // both dismisses.
  useEffect(() => {
    if (!open) return undefined;
    const onOutsideClick = (event: globalThis.MouseEvent): void => {
      const target = event.target as Node;
      const insideTrigger = rootRef.current?.contains(target) ?? false;
      const insideMenu = popoverRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insideMenu) {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener('click', onOutsideClick, true);
    return () => document.removeEventListener('click', onOutsideClick, true);
  }, [open, close]);

  // FR-020: opening the menu autofocuses the filter input (no-op in the
  // all-assigned state, where there is no input).
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // #150: measure the trigger and pin the portaled menu to it. Lives in a
  // layout effect so the position is set before the browser paints (no flash).
  const positionMenu = useCallback((): void => {
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const rect = trigger.getBoundingClientRect();
    // FR-008: if the trigger has scrolled fully out of view, close rather than
    // leave the menu floating detached from it.
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      close();
      return;
    }
    setMenuStyle(computeMenuStyle(rect, window.innerWidth, window.innerHeight));
  }, [close]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    positionMenu();
    // FR-008 / SC-006: while open, keep the menu anchored to its trigger on any
    // scroll (capture phase, to catch an ancestor scroll container) or resize —
    // it re-anchors or closes, but never strands itself detached mid-screen.
    const onReposition = (): void => positionMenu();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, positionMenu]);

  const hasMatrix = allowedRoles.length > 0;
  // F-040 US3: the matrix is required since F-021, so an empty Allowed-Role Set no
  // longer shows the obsolete "upload a matrix" prompt. Pre-filled roles render
  // read-only (no add/remove); a role-less case with no matrix renders nothing
  // (the parent's "Role(s)" label already conveys the emptiness).
  if (!hasMatrix && roles.length === 0) return null;
  const assignable = allowedRoles.filter((role) => !roles.includes(role));
  // FR-006/007: narrow ONLY the assignable set by case-insensitive substring;
  // never offer or create anything outside the Allowed-Role Set (no free-text).
  const query = filter.trim().toLowerCase();
  const filtered =
    query === '' ? assignable : assignable.filter((role) => role.toLowerCase().includes(query));
  const activeId =
    activeIndex >= 0 && activeIndex < filtered.length
      ? `${listboxId}-opt-${activeIndex}`
      : undefined;
  const suffix = label ? ` to ${label}` : '';
  const stop = (event: MouseEvent) => event.stopPropagation();

  const add = (role: string): void => {
    onChange([...roles, role]);
    close();
  };
  const remove = (role: string): void => onChange(roles.filter((r) => r !== role));

  // FR-002: Escape closes the menu and returns focus to the trigger. Attached to
  // the menu wrapper so it catches Escape from the trigger or any menu child.
  const onMenuKeyDown = (event: KeyboardEvent): void => {
    // Only the OPEN menu owns Escape — when closed it bubbles so an ancestor
    // (e.g. a Dialog) can still handle it (FR-002).
    if (open && event.key === 'Escape') {
      event.stopPropagation();
      close();
      triggerRef.current?.focus();
    }
  };

  // FR-009/020: keyboard operation from the focused filter input. The active
  // option is tracked by index (→ aria-activedescendant), clamped in range;
  // Enter adds it. Escape is left to bubble up to `onMenuKeyDown`.
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const role = filtered[activeIndex];
      if (role !== undefined) add(role);
    }
  };

  // F-034: in the detail rail (`full`) the empty add control reads as a
  // labelled "Add Role(s)" placeholder filling the field; once a role is
  // assigned it collapses to the compact `+` glyph beside the chips. The list
  // (`compact`) entry point always uses the bare glyph.
  const showAddLabel = variant === 'full' && roles.length === 0;

  const chips = roles.map((role) => {
    const offMatrix = hasMatrix && !allowedRoles.includes(role);
    return (
      <span
        key={role}
        // Out-of-matrix carries a NON-colour cue (dashed border + word) as well
        // as the amber tint, and is kept — never silently removed (FR-032/AS8).
        title={
          offMatrix
            ? `${role} — not in the current Roles & Permissions Matrix (kept, not removed)`
            : role
        }
        aria-label={offMatrix ? `${role} (off-matrix)` : role}
        className={clsx(
          'inline-flex min-w-0 items-center leading-none',
          // Compact (assembly table cell): a 21px fully-rounded blue pill that
          // fills the cell (#4C70F0/15 fill + #515FFC text). Read-only here.
          variant === 'compact'
            ? 'h-[21px] flex-1 justify-center gap-[4px] rounded-[44px] px-[6px] py-[2px]'
            : 'h-6 gap-1 rounded-sm px-1.5 py-1 text-xs font-semibold',
          offMatrix
            ? 'border border-dashed border-warning-fg bg-warning-bg text-warning-fg'
            : variant === 'compact'
              ? 'bg-[#4c70f0]/15 text-[#515ffc]'
              : 'bg-purple-100 text-purple-800',
        )}
      >
        <span
          className={clsx(
            'truncate',
            variant === 'compact' && 'text-[14px] font-normal leading-[1.4] tracking-[-0.14px]',
          )}
        >
          {role}
        </span>
        {offMatrix && <span className="text-[10px] font-bold uppercase">⚠</span>}
        {/* The remove (×) control is offered only in the detail-rail (`full`)
            chooser (and only with a matrix); the compact table pill is read-only
            — a tap falls through to the row and opens the test case. */}
        {variant === 'full' && hasMatrix && (
          <button
            type="button"
            aria-label={`Remove ${role}`}
            onClick={(event) => {
              stop(event);
              remove(role);
            }}
            className="inline-flex items-center border-0 bg-transparent p-0 text-inherit"
          >
            <Icon name="close" size={10} />
          </button>
        )}
      </span>
    );
  });

  const addControl = hasMatrix ? (
    <div className={clsx('relative', showAddLabel && 'flex-1')} onKeyDown={onMenuKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`Add role${suffix}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(event) => {
          stop(event);
          // FR-003: re-clicking the trigger dismisses via close(), which also
          // discards any typed filter so the menu reopens clean.
          if (open) close();
          else setOpen(true);
        }}
        className={clsx(
          'inline-flex cursor-pointer items-center gap-1 border-0',
          showAddLabel
            ? 'h-6 w-full justify-start rounded-sm bg-ink-100 px-2 text-text-secondary hover:bg-purple-100 hover:text-purple-700'
            : variant === 'compact'
              ? // Compact empty cell: a 21px #4C70F0/15 circle with a #515FFC plus.
                'h-[21px] w-[21px] justify-center rounded-full bg-[#4c70f0]/15 text-[#515ffc] hover:bg-[#4c70f0]/25'
              : 'h-6 w-6 justify-center rounded-sm bg-ink-100 text-text-secondary hover:bg-purple-100 hover:text-purple-700',
        )}
      >
        <Icon name="plus" size={12} />
        {showAddLabel && <span className="text-sm font-normal">Add Role(s)</span>}
      </button>
      {open &&
        // #150 / FR-001/002: portal the menu to document.body so its stacking no
        // longer depends on the trigger cell's `relative z-[1]` context, and pin it
        // `fixed` to the trigger (measured into `menuStyle`). FR-004/005: opaque
        // elevated surface (`bg-surface-card` = #FFFFFF; the bare `bg-surface` token
        // has no DEFAULT and rendered see-through).
        createPortal(
          <div
            ref={popoverRef}
            data-role-popover=""
            // The portaled surface is "inside" the chooser: swallow its clicks so
            // they never reach a parent row (FR-005), and own Escape directly so
            // the menu closes regardless of React's portal event propagation (the
            // wrapper's onMenuKeyDown also still catches it — belt and suspenders).
            onClick={stop}
            onKeyDown={onMenuKeyDown}
            style={menuStyleToCss(menuStyle)}
            className="z-50 min-w-[200px] overflow-auto rounded-md border border-border bg-surface-card p-1 shadow-md"
          >
            {assignable.length === 0 ? (
              // FR-017: every allowed role already assigned — no filter input.
              <p className="px-2 py-1 text-xs text-text-secondary">All study roles assigned</p>
            ) : (
              <>
                {/* FR-019/020/021: a WCAG combobox — autofocused filter input wired
                  to the listbox via aria-controls + aria-activedescendant. */}
                <input
                  ref={inputRef}
                  type="text"
                  role="combobox"
                  aria-expanded
                  aria-controls={listboxId}
                  aria-autocomplete="list"
                  aria-activedescendant={activeId}
                  aria-label={`Filter roles${suffix}`}
                  value={filter}
                  placeholder="Filter roles"
                  onClick={stop}
                  onChange={(event) => {
                    setFilter(event.target.value);
                    setActiveIndex(0);
                  }}
                  onKeyDown={onInputKeyDown}
                  className="mb-1 block w-full rounded-sm border border-border bg-white px-2 py-1 text-sm text-text-primary outline-none"
                />
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-label={`Study roles${suffix}`}
                  className="m-0 max-h-56 list-none overflow-auto p-0"
                >
                  {filtered.length === 0 ? (
                    // FR-008: filter matched nothing (assignable roles still exist).
                    <li className="px-2 py-1 text-xs text-text-secondary">No matching roles</li>
                  ) : (
                    filtered.map((role, index) => (
                      <li key={role}>
                        <button
                          type="button"
                          role="option"
                          id={`${listboxId}-opt-${index}`}
                          aria-selected={index === activeIndex}
                          onClick={(event) => {
                            stop(event);
                            add(role);
                          }}
                          // FR-015: pointer and keyboard share ONE active option —
                          // hovering sets the active index, so the highlight (and
                          // aria-activedescendant) tracks both inputs identically.
                          onMouseEnter={() => setActiveIndex(index)}
                          className={clsx(
                            'flex w-full cursor-pointer items-center rounded-sm border-0 px-2 py-1 text-left text-sm',
                            index === activeIndex
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-transparent text-text-primary hover:bg-purple-100 hover:text-purple-800',
                          )}
                        >
                          {role}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  ) : // F-040 US3: no matrix ⇒ no add control (the chips above are read-only).
  null;

  // `compact` (list table cell): once a role is assigned the cell shows only the
  // role pill(s) — the "+" add affordance is offered only while empty. The
  // wrapper does NOT stop propagation, so tapping an assigned pill falls through
  // to the row and opens the test case (the "+" / dropdown stop on their own).
  if (variant === 'compact') {
    return (
      <div
        ref={rootRef}
        data-role-chooser={label ?? ''}
        className="flex min-w-0 items-center gap-1"
      >
        {chips}
        {roles.length === 0 && addControl}
      </div>
    );
  }

  // `full` (detail rail): a full-width bordered field — chips wrap inside and
  // the add control fills the row when empty (Figma "Add Role(s)").
  return (
    <div ref={rootRef} data-role-chooser={label ?? ''} onClick={stop} className="w-full">
      <div className="flex min-h-9 w-full flex-wrap items-center gap-1 rounded-sm border border-border bg-white px-2 py-1.5">
        {chips}
        {addControl}
      </div>
    </div>
  );
}
